import { decodeToken, isTokenExpired } from '../../session/decode';
import { readSsoCookies, writeSsoCookies } from '../../tokens/cookies';
import { removeAccessAndIdTokens } from '../../tokens/localStorage';
import type { ResolvedStorageKeys } from '../constants/storageKeys';
import type { SsoConfig } from '../types';
import {
  getKeyedTokens,
  setKeyedRefreshToken,
  setKeyedTokens,
} from './keyedStorage';

export const isAccessTokenLogged = (
  config: SsoConfig,
  keys: ResolvedStorageKeys
): boolean => {
  if (config.isExternalPath()) {
    return true;
  }

  const { accessToken } = getKeyedTokens(keys);
  const decodedToken = accessToken ? decodeToken(accessToken) : null;

  if (!decodedToken?.exp) {
    return false;
  }

  return !isTokenExpired(decodedToken.exp);
};

export const isRequestingLocalSignIn = (
  apiEnv: string | undefined | null
): boolean => {
  const { refreshToken } = readSsoCookies(apiEnv);
  return refreshToken !== undefined;
};

export const clearSsoUrl = (apiEnv: string | undefined | null) => {
  const { refreshToken } = readSsoCookies(apiEnv);

  if (refreshToken)
    window.history.replaceState(null, '', window.location.pathname);
};

export const applyCookieHandoff = (
  keys: ResolvedStorageKeys,
  apiEnv: string | undefined | null
): { accessToken: string; idToken: string } | null => {
  removeAccessAndIdTokens();

  const { refreshToken, accessToken, idToken } = readSsoCookies(apiEnv);

  if (!accessToken || !idToken) return null;
  if (refreshToken) setKeyedRefreshToken(keys, refreshToken);

  setKeyedTokens(keys, accessToken, idToken);
  clearSsoUrl(apiEnv);

  return { accessToken, idToken };
};

export const localSignIn = (
  keys: ResolvedStorageKeys,
  accessToken: string,
  idToken: string,
  apiEnv: string | undefined | null
) => {
  setKeyedTokens(keys, accessToken, idToken);
  const { refreshToken } = getKeyedTokens(keys);
  writeSsoCookies(
    {
      accessToken,
      idToken,
      refreshToken: refreshToken ?? undefined,
    },
    apiEnv
  );
  clearSsoUrl(apiEnv);
};
